/** Turning a stored lap into the two drawings the Sessions screen makes of it.
 *
 * A lap document arrives as parallel arrays — a racing line reduced to a few
 * thousand points, and the 60 Hz channels it was reduced from — and the screen
 * wants two pictures out of that: the line on a map, and speed against
 * distance. Both are pure arithmetic on those arrays, so all of it lives here
 * and none of it lives in a component; a chart that is wrong should be
 * debuggable without a browser.
 *
 * Three decisions are worth stating because they are not obvious from the
 * code:
 *
 *  - The road under the lines is the published circuit where there is one:
 *    the compiler's road quads, at their measured width, with the two borders
 *    over them. A session holds no survey geometry of its own — that is the
 *    whole point of keeping the two apart — so the outline is asked for by
 *    layout id from the public tracks side, which says nothing about whose
 *    drive it is drawn under (the page already asks that side who else has
 *    driven the circuit). Where nobody has published one, the laps themselves
 *    are the bed, stroked thickly underneath the lines: honest, and the only
 *    road available, but a road that follows the line wherever it went, so
 *    the card says which of the two it is showing.
 *  - The start/finish tick is taken from the line's own first point. A lap
 *    starts and ends at the line, so its first sample is the crossing, and
 *    the tick is drawn square across the direction the car was travelling.
 *  - Sector labels sit at the middle of their sector rather than at its
 *    boundary, because the label names a stretch of circuit and a boundary
 *    would put "S2" where sector one ended.
 *
 * Everything is in world metres with `z` negated, which is the same space
 * `TrackOutline` draws a circuit in — the fitting, the path building and the
 * bounding boxes are that module's, imported rather than written twice, so a
 * lap and a track outline can never disagree about which way up the world is.
 */

import {
  boundsOfPoints,
  fitViewBox,
  geometryBounds as outlineBounds,
  hasGeometry,
  pathFromPoints,
  pathsFromGeometry,
  unionBox,
  viewBoxString,
} from '@/components/TrackOutline';
import type { Box, Point, ViewBox } from '@/components/TrackOutline';
import type { LapDocument, RacingLine, TrackGeometry } from '@/lib/types';

export type { Point, ViewBox };
export { hasGeometry, pathFromPoints, pathsFromGeometry, viewBoxString };

/** How many samples ahead the start tick looks to find a heading. One sample
 *  at 60 Hz is a couple of centimetres and its direction is mostly noise. */
const HEADING_LOOKAHEAD = 8;

function finite(value: number | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

// ── the racing line ─────────────────────────────────────────────────────────

/** Parallel arrays to drawing space: world `x` across, world `z` negated, so
 *  the map reads the same way up as the one in the game. */
export function linePoints(line: RacingLine | null | undefined): Point[] {
  if (!line) return [];
  const count = Math.min(line.x.length, line.z.length);
  const points: Point[] = [];
  for (let i = 0; i < count; i += 1) {
    const x = line.x[i];
    const z = line.z[i];
    if (!finite(x) || !finite(z)) continue;
    points.push([x, -z]);
  }
  return points;
}

/** The line a lap draws. The stored line is preferred; the position channels
 *  are the fallback for a document whose line did not survive. */
export function lapLine(document: LapDocument | null | undefined): Point[] {
  if (!document) return [];
  const drawn = linePoints(document.lap.line);
  if (drawn.length >= 2) return drawn;
  const x = document.lap.samples['pos_x'];
  const z = document.lap.samples['pos_z'];
  if (!x || !z) return [];
  return linePoints({ x, z });
}

export interface LineFit {
  view: ViewBox;
  /** Text inside a fitted viewBox is sized in that box's own units, so a
   *  20 km circuit does not render its labels a hundredth of a millimetre
   *  tall. */
  labelSize: number;
}

/** One viewBox that holds every line being drawn — and the circuit they were
 *  driven on, when it is published — so two laps of the same circuit are
 *  compared rather than each fitted to itself, and a lap is seen against the
 *  whole road rather than only the part of it the lap reached. */
export function fitLines(
  groups: ReadonlyArray<readonly Point[]>,
  outline: TrackGeometry | null = null,
): LineFit | null {
  let box: Box | null = null;
  for (const points of groups) box = unionBox(box, boundsOfPoints(points));
  if (outline && hasGeometry(outline)) box = unionBox(box, outlineBounds(outline));
  if (!box) return null;
  const view = fitViewBox(box, 0.08);
  return { view, labelSize: view.width * 0.028 };
}

// ── zooming the map ─────────────────────────────────────────────────────────

/** How far in the map may be taken. A whole 4 km oval fitted to a card puts
 *  its 8 m road under a pixel, and the question a racing line answers — inside
 *  or outside, early or late — needs the road a hand's width across. Eighty
 *  times gets a corner to that; further and the picture is a line on a blank. */
export const MAX_ZOOM = 80;

/** How many times closer than the fitted picture a view is. */
export function zoomOf(base: ViewBox, view: ViewBox): number {
  return base.width / view.width;
}

/** A view kept inside the fitted one, so a drag cannot lose the circuit off
 *  the edge of the card and a zoom out stops at the whole picture. */
export function clampView(base: ViewBox, view: ViewBox): ViewBox {
  const width = Math.min(view.width, base.width);
  const height = Math.min(view.height, base.height);
  return {
    x: Math.min(Math.max(view.x, base.x), base.x + base.width - width),
    y: Math.min(Math.max(view.y, base.y), base.y + base.height - height),
    width,
    height,
  };
}

/** The view zoomed by `factor` (above one is in) about a point in drawing
 *  space, which stays where it was on screen: the viewBox keeps the fitted
 *  aspect, so a point at the same fraction of it is at the same pixel. Held
 *  between the fit and `MAX_ZOOM`. */
export function zoomView(base: ViewBox, view: ViewBox, factor: number, about: Point): ViewBox {
  const target = Math.min(MAX_ZOOM, Math.max(1, zoomOf(base, view) * factor));
  const width = base.width / target;
  const height = base.height / target;
  const fx = (about[0] - view.x) / view.width;
  const fy = (about[1] - view.y) / view.height;
  return clampView(base, { x: about[0] - fx * width, y: about[1] - fy * height, width, height });
}

/** The view moved by a drag, in drawing units. */
export function panView(base: ViewBox, view: ViewBox, dx: number, dy: number): ViewBox {
  return clampView(base, { ...view, x: view.x + dx, y: view.y + dy });
}

/** The road surface as the compiler's quads — `[x1, z1, … x4, z4]`, corners
 *  in order round the quad — each a closed subpath in drawing space. A quad
 *  with a number missing is left out rather than drawn with a corner at the
 *  origin. */
export function roadPath(geometry: TrackGeometry): string {
  const parts: string[] = [];
  for (const quad of geometry.road ?? []) {
    if (quad.length < 8) continue;
    const corners: string[] = [];
    for (let i = 0; i < 8; i += 2) {
      const x = quad[i];
      const z = quad[i + 1];
      if (!finite(x) || !finite(z)) break;
      corners.push(`${x.toFixed(2)} ${(-z).toFixed(2)}`);
    }
    if (corners.length === 4) parts.push(`M ${corners.join(' L ')} Z`);
  }
  return parts.join(' ');
}

/** The start/finish tick: a segment square across the line at its first
 *  point, `length` metres end to end. */
export function startTick(points: readonly Point[], length: number): [Point, Point] | null {
  const start = points[0];
  if (!start) return null;
  const ahead = points[Math.min(HEADING_LOOKAHEAD, points.length - 1)];
  if (!ahead) return null;

  const dx = ahead[0] - start[0];
  const dy = ahead[1] - start[1];
  const size = Math.hypot(dx, dy);
  if (size === 0) return null;

  const halfX = (-dy / size) * (length / 2);
  const halfY = (dx / size) * (length / 2);
  return [
    [start[0] - halfX, start[1] - halfY],
    [start[0] + halfX, start[1] + halfY],
  ];
}

/** The clock a lap's channels are measured against, in milliseconds. */
function sampleTimesMs(document: LapDocument): number[] | null {
  const seconds = document.lap.samples['t'];
  if (!seconds || seconds.length < 2) return null;
  return seconds.map((value) => value * 1000);
}

/** The first index at or past a moment in the lap. */
function indexAtMs(times: readonly number[], ms: number): number {
  for (let i = 0; i < times.length; i += 1) {
    const at = times[i];
    if (finite(at) && at >= ms) return i;
  }
  return times.length - 1;
}

/**
 * Where each sector sits on the drawn line.
 *
 * The stored line is every n-th sample of a recording taken at a steady rate,
 * so a fraction of the way through the lap's time is a fraction of the way
 * along the line's points — which is what makes the fallback below accurate
 * rather than merely plausible. Where the document still carries its position
 * channels the moment is looked up in them directly, which is exact even for
 * a line a client supplied itself.
 */
export function sectorMarks(
  document: LapDocument | null | undefined,
  points: readonly Point[],
): Array<{ label: string; point: Point }> {
  if (!document || points.length < 2) return [];
  const sectors = document.lap.sector_ms.filter((ms) => finite(ms) && ms > 0);
  if (sectors.length === 0) return [];

  const total = sectors.reduce((sum, ms) => sum + ms, 0);
  const times = sampleTimesMs(document);
  const x = document.lap.samples['pos_x'];
  const z = document.lap.samples['pos_z'];
  const exact = times && x && z && x.length === times.length && z.length === times.length;

  const marks: Array<{ label: string; point: Point }> = [];
  let elapsed = 0;
  sectors.forEach((ms, index) => {
    const middle = elapsed + ms / 2;
    elapsed += ms;

    let point: Point | undefined;
    if (exact && times) {
      const at = indexAtMs(times, middle);
      const px = x[at];
      const pz = z[at];
      if (finite(px) && finite(pz)) point = [px, -pz];
    }
    if (!point) {
      const along = Math.round((middle / total) * (points.length - 1));
      point = points[Math.min(Math.max(along, 0), points.length - 1)];
    }
    if (point) marks.push({ label: `S${index + 1}`, point });
  });
  return marks;
}

// ── speed against distance ──────────────────────────────────────────────────

/** A lap's speed trace: km/h as the logger records it, against metres along
 *  the lap. */
export interface SpeedTrace {
  distance: number[];
  speed: number[];
}

/** Metres along the lap, from the channel that states it or from the speed
 *  and the clock when it is absent. */
function distanceChannel(document: LapDocument, ticks: number): number[] | null {
  const declared = document.lap.samples['dist'];
  if (declared && declared.length === ticks) return declared;

  const times = document.lap.samples['t'];
  const speed = document.lap.samples['speed'];
  if (!times || !speed || times.length !== ticks) return null;

  const metres = new Array<number>(ticks).fill(0);
  for (let i = 1; i < ticks; i += 1) {
    const dt = (times[i] ?? 0) - (times[i - 1] ?? 0);
    const kmh = speed[i] ?? 0;
    metres[i] = (metres[i - 1] ?? 0) + (dt > 0 ? (kmh / 3.6) * dt : 0);
  }
  return metres;
}

export function speedTrace(document: LapDocument | null | undefined): SpeedTrace | null {
  if (!document) return null;
  const speed = document.lap.samples['speed'];
  if (!speed || speed.length < 2) return null;
  const distance = distanceChannel(document, speed.length);
  if (!distance) return null;

  const trace: SpeedTrace = { distance: [], speed: [] };
  for (let i = 0; i < speed.length; i += 1) {
    const metres = distance[i];
    const kmh = speed[i];
    if (!finite(metres) || !finite(kmh)) continue;
    trace.distance.push(metres);
    trace.speed.push(kmh);
  }
  return trace.speed.length >= 2 ? trace : null;
}

export interface SpeedDomain {
  maxDistance: number;
  low: number;
  high: number;
}

/** One pair of scales for every trace on the chart, padded so the quickest
 *  lap does not touch the top of the box. */
export function speedDomain(traces: ReadonlyArray<SpeedTrace | null>): SpeedDomain | null {
  let maxDistance = 0;
  let low = Number.POSITIVE_INFINITY;
  let high = Number.NEGATIVE_INFINITY;

  for (const trace of traces) {
    if (!trace) continue;
    for (const metres of trace.distance) if (metres > maxDistance) maxDistance = metres;
    for (const kmh of trace.speed) {
      if (kmh < low) low = kmh;
      if (kmh > high) high = kmh;
    }
  }
  if (!Number.isFinite(low) || !Number.isFinite(high) || maxDistance <= 0) return null;

  const pad = Math.max((high - low) * 0.08, 1);
  return { maxDistance, low: Math.max(0, low - pad), high: high + pad };
}

export interface ChartBox {
  width: number;
  height: number;
  /** Room left above the highest point and below the lowest. */
  padTop: number;
  padBottom: number;
}

export const SPEED_BOX: ChartBox = { width: 400, height: 120, padTop: 6, padBottom: 2 };

/** A trace as an SVG `points` list. */
export function speedPolyline(
  trace: SpeedTrace | null,
  domain: SpeedDomain | null,
  box: ChartBox = SPEED_BOX,
): string {
  if (!trace || !domain) return '';
  const span = domain.high - domain.low || 1;
  const usable = box.height - box.padTop - box.padBottom;
  const out: string[] = [];
  for (let i = 0; i < trace.speed.length; i += 1) {
    const metres = trace.distance[i] ?? 0;
    const kmh = trace.speed[i] ?? 0;
    const x = (metres / domain.maxDistance) * box.width;
    const y = box.height - box.padBottom - ((kmh - domain.low) / span) * usable;
    out.push(`${x.toFixed(1)},${y.toFixed(1)}`);
  }
  return out.join(' ');
}

/** Where the sector boundaries fall along the distance axis, for the dashed
 *  verticals. The last boundary is the line itself and is left out. */
export function sectorDistances(
  document: LapDocument | null | undefined,
  trace: SpeedTrace | null,
): number[] {
  if (!document || !trace) return [];
  const sectors = document.lap.sector_ms.filter((ms) => finite(ms) && ms > 0);
  if (sectors.length < 2) return [];

  const total = sectors.reduce((sum, ms) => sum + ms, 0);
  const times = sampleTimesMs(document);
  const usable = times && times.length === trace.speed.length;

  const out: number[] = [];
  let elapsed = 0;
  for (let i = 0; i < sectors.length - 1; i += 1) {
    elapsed += sectors[i] as number;
    let at: number;
    if (usable && times) {
      at = indexAtMs(times, elapsed);
    } else {
      at = Math.round((elapsed / total) * (trace.distance.length - 1));
    }
    const metres = trace.distance[Math.min(Math.max(at, 0), trace.distance.length - 1)];
    if (finite(metres)) out.push(metres);
  }
  return out;
}

export function peakSpeed(trace: SpeedTrace | null): number | null {
  if (!trace || trace.speed.length === 0) return null;
  return trace.speed.reduce((best, kmh) => (kmh > best ? kmh : best), trace.speed[0] as number);
}

/** The four figures written under the chart: the line, two thirds of the way
 *  round, and the line again. */
export function distanceTicks(maxDistance: number): number[] {
  if (!(maxDistance > 0)) return [];
  return [0, maxDistance / 3, (maxDistance * 2) / 3, maxDistance];
}
